import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { humanizeTimestamp } from '../utils/date'
import { ActionRequest, Project } from '../types'
import ActionRequestAcceptButton from './ActionRequestAcceptButton'
import { useState } from 'react'
import countryList from '../lists/countryList.json'
import { useDialog } from '../hooks'
import ActionRequestInfoList from './ActionRequestInfoList'

type SelectCountryCodeButtonProps = {
  actionRequest: ActionRequest
  project: Project
  handleAfterSubmit?: () => void
}

export default function SelectCountryCodeButton({
  actionRequest,
  project,
  handleAfterSubmit
}: SelectCountryCodeButtonProps) {
  const { t } = useTranslation('common')

  const { open, openDialog, closeDialog } = useDialog()

  const [selectedCountryCode, setSelectedCountryCode] = useState<string | undefined>(undefined)

  const title = t('SelectCountryCode.title', { ns: 'actionRequest' })
  const timestamp = new Date(actionRequest.createdAt)

  return (
    <>
      <Button onClick={openDialog}>{t('SelectCountryCode.button', { ns: 'actionRequest' })}</Button>
      <Dialog open={open} maxWidth="md" fullWidth onClose={(_, __) => closeDialog()}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title}
            <Typography color="disabled">{humanizeTimestamp(timestamp)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            {t('SelectCountryCode.info', {
              projectName: project.name,
              ns: 'actionRequest'
            })}
          </Alert>
          <ActionRequestInfoList actionRequest={actionRequest} sx={{ mt: 1 }} />
          <Box sx={{ mt: 2, mb: 2 }}>
            <Autocomplete
              options={countryList.map((option) => option.code)}
              autoHighlight
              getOptionLabel={(option) => option}
              onChange={(_, value) => {
                setSelectedCountryCode(value ?? undefined)
              }}
              renderOption={(props, option) => (
                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                  <img
                    loading="lazy"
                    width="20"
                    srcSet={`https://flagcdn.com/w40/${option.toLowerCase()}.png 2x`}
                    src={`https://flagcdn.com/w20/${option.toLowerCase()}.png`}
                    alt=""
                  />
                  ({option})
                </Box>
              )}
              renderInput={(params) => <TextField {...params} label={t('countryCode')} />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={closeDialog}>
            {t('cancel')}
          </Button>

          <ActionRequestAcceptButton
            variant="contained"
            color="success"
            actionRequest={actionRequest}
            afterSubmit={() => {
              handleAfterSubmit?.()
              closeDialog()
            }}
            submitInput={{ countryCode: selectedCountryCode }}
            disabled={!selectedCountryCode}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
